import { SelectChangeEvent } from "@mui/material";

/**
 * Creates a multi-select change handler function that manages selection state,
 * including a special "ALL" option that selects or deselects all choices.
 *
 * @param getPrevSelected - Function that returns the currently selected options array.
 * @param setSelected - Function to update the selected options.
 * @param options - Array of all options where the first entry is treated as "ALL".
 * @returns A change event handler function to be used with a MUI Select component.
 */
export function makeMultiSelectChangeHandler(
  getPrevSelected: () => string[],
  setSelected: (values: string[]) => void,
  options: string[]
) {
  return (e: SelectChangeEvent<string[]>) => {
    // Get the new value(s) from the event, normalize to array if string
    const value = e.target.value;
    const newValues = typeof value === 'string' ? value.split(',') : value;

    // Get previously selected options
    const prevSelected = getPrevSelected();

    // The "ALL" option is assumed to be the first item in options array
    const ALL = options[0];

    // Initialize state tracking variables
    let checked = false;
    // Find which option changed by checking which was removed
    let changedOption = prevSelected.find((v) => !newValues.includes(v)) || ALL;

    let finalValues: string[];

    // If new selection count is greater, an option was added (checked)
    if (newValues.length > prevSelected.length) {
      checked = true;
      // Find which new option was added
      changedOption = newValues.find((v) => !prevSelected.includes(v)) || ALL;
    }

    // Remove the changed option and "ALL" from the previous selection initially
    finalValues = prevSelected.filter((opt) => opt !== changedOption && opt !== ALL);

    // If previously all options were selected but now one (not ALL) was unchecked,
    // remove that changed option
    if (prevSelected.length === options.length && !checked) {
      finalValues = prevSelected.filter((opt) => opt !== ALL && opt !== changedOption);
    }

    // If an option was checked
    if (checked) {
      // Add the changed option to the selection
      const next = [...prevSelected, changedOption];
      // If almost all except one option is selected, select all options instead
      finalValues = next.length === options.length - 1 ? [...options] : next;
    }

    // If the changed option is the special "ALL" option,
    // select all or clear all depending on whether it was checked or unchecked
    if (changedOption === ALL) {
      finalValues = checked ? [...options] : [];
    }
    // Update selection using provided setter function
    setSelected(finalValues);
  };
}
